import React, {
  ComponentProps,
  useEffect,
  useMemo,
  useState,
} from "react";
import SvgIcon from "../SvgIcon/SvgIcon";
import generateComponentName from "../utils/generateComponentName";
import { loadSvgIconModule } from "./loadSvgIconModule";
import { syncSvgIconComponents } from "./syncCriticalSvgIcons";

export const ComponentIcon = (block: {
  name: string;
  key?: any;
  props?: ComponentProps<typeof SvgIcon>;
}) => {
  const { name, key, props } = block;
  const componentName = generateComponentName(name);

  if (!componentName) {
    return React.createElement(
      () => (
        <div>The component for «{name}» has not been created yet.</div>
      ),
      { key }
    );
  }

  const Sync = syncSvgIconComponents[componentName];
  if (Sync) {
    return React.createElement(Sync, {
      key,
      ...props,
    });
  }

  return (
    <LazySvgIcon
      key={key}
      componentName={componentName}
      iconProps={props}
    />
  );
};

function LazySvgIcon({
  componentName,
  iconProps,
}: {
  componentName: string;
  iconProps?: ComponentProps<typeof SvgIcon>;
}) {
  const [Comp, setComp] = useState<React.ComponentType<
    ComponentProps<typeof SvgIcon>
  > | null>(null);
  const [missing, setMissing] = useState(false);

  const size = iconProps?.size ?? 24;

  useEffect(() => {
    let cancelled = false;
    setComp(null);
    setMissing(false);
    loadSvgIconModule(componentName).then((m) => {
      if (cancelled) return;
      if (m?.default) {
        setComp(() => m.default as React.ComponentType<any>);
      } else {
        setMissing(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [componentName]);

  const placeholderStyle = useMemo(
    () =>
      ({
        display: "inline-block",
        width: size,
        height: size,
        verticalAlign: "middle",
      }) as const,
    [size]
  );

  if (missing) {
    return (
      <div>
        The component {componentName} has not been created yet.
      </div>
    );
  }

  if (Comp) {
    return React.createElement(Comp, { ...iconProps });
  }

  return (
    <span aria-hidden className="svg-icon-pending" style={placeholderStyle} />
  );
}

// @ts-ignore
export type IconName = "menu"
  | "home"
  | "search"
  | "filters"
  | "star"
  | "star_fill"
  | "star_fill_half"
  | "circle_fill"
  | "bullet_in_circle"
  | "check_mark"
  | "plus"
  | "minus"
  | "reorder"
  | "arrow_long_left"
  | "arrow_long_right"
  | "arrowhead_up"
  | "arrowhead_down"
  | "caret_down"
  | "clock"
  | "pin"
  | "pin_outline"
  | "arrow_small_left"
  | "arrow_small_right"
  | "address_house"
  | "address_work"
  | "favoris"
  | "favoris_fill"
  | "etoiles"
  | "checkmark_fat"
  | "message"
  | "cart"
  | "user"
  | "logo_sushi_shop"
  | "logo_comein_ichi"
  | "logo_comein_ni"
  | "logo_comein_san"
  | "store"
  | "caret_down_16"
  | "logo_facebook"
  | "logo_instagram"
  | "logo_linkedin"
  | "logo_twitter"
  | "cross_close"
  | "geoloc"
  | "switch_arrow"
  | "check_circle_fill"
  | "calendar"
  | "tag"
  | "trash"
  | "information_circle_fill"
  | "logo_tiktok"
  | "click_and_collect"
  | "address_holidays"
  | "address_friends"
  | "address_family"
  | "empty_cart"
  | "instagram"
  | "plus_circle"
  | "play"
  | "logo_twitter_x"
  | "delivery"
  | "customer_service"
  | "phone"
  | "caret_right_16"
  | "caret_right_bold_16"
  | "edit"
  | "download"
  | "check_circle"
  | "wallet"
  | "credit_card"
  | "gift"
  | "copy_paste"
  | "birthday"
  | "logo_pinterest"
  | "list"
  | "exclamation_circle"
  | "cadeau"
  | "cagnotte_euro"
  | "anniversaire"
  | "explorateur"
  | "epicurien"
  | "gourmet"
  | "padlock_bold"
  | "eye"
  | "eye_off"
  | "padlock"
  | "check_mark_16"
  | "caret_down_bold_16"
  | "plus_bold_16"
  | "minus_bold_16"
  | "star_fill_rounded"
  | "logo_my_sushi_shop"
  | "logo_my_simple"
  | "confettis_bold"
  | "gift_bold"
  | "wallet_bold"
  | "star_explorateur"
  | "star_gourmet"
  | "star_epicurien"
  | "menu_list"
  | "fidelity"
  | "press_bookmark"
  | "clipboard"
  | "geoloc_arrow"
  | "small_cross_close"
  | "exclamation_circle_fill"
  | "device"
  | "home_96"
  | "app"
  | "explorateur_basic"
  | "gourmet_basic"
  | "epicurien_basic"
  | "cagnotte_badge";
// @ts-ignore
export type FontIconName = Exclude<IconName, "">;
